import { useMemo, useRef, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

/**
 * Column ids whose values are textual and therefore sort alphabetically (A–Z).
 * Every other column is treated as numeric (Decimal strings from the API).
 * `directs_covered` is the YES/NO flag, so it's textual too. `supplier_name`
 * is the Supplier column of Stock Planning's all-suppliers grid (CR-8193), and
 * `remarks` the customer's notes on it (CR-8624).
 */
export const DEFAULT_TEXT_COLUMNS: readonly string[] = [
  'part_number',
  'description',
  'commodity_type',
  'directs_covered',
  'supplier_name',
  'remarks',
];

export interface UseTableSort<T> {
  /** Column id currently sorted by, or null when unsorted. */
  sortColumn: string | null;
  sortDirection: SortDirection;
  /** `items` reordered by the active column (input order when unsorted). */
  sortedItems: T[];
  /**
   * Debounced single-click handler: sort by this column (numeric high→low,
   * text A–Z; click the active column again to flip direction).
   */
  handleHeaderClick: (columnId: string) => void;
  /** Double-click handler: jump straight to negatives-first (ascending). */
  handleHeaderDoubleClick: (columnId: string) => void;
  /** aria-sort value for a column header. */
  getAriaSort: (columnId: string) => 'ascending' | 'descending' | 'none';
}

/**
 * Spreadsheet-style client-side column sort for a grid of row objects keyed by
 * column id.
 *
 * Single-click a header to sort (numeric high→low, text A–Z; click again to
 * flip). Double-click surfaces negative values at the top — parts in deficit
 * that need calling off first float up. Shared by the Stock Planning grid and
 * the warehouse call-off wizard. CR-6296.
 *
 * @param items - The rows to sort. Values are read by column id.
 * @param textColumns - Column ids sorted alphabetically; the rest sort numerically.
 * @returns Sort state, the sorted rows, and header click/double-click handlers.
 */
export function useTableSort<T>(
  items: T[],
  textColumns: readonly string[] = DEFAULT_TEXT_COLUMNS,
): UseTableSort<T> {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const textSet = useMemo(() => new Set(textColumns), [textColumns]);
  // A double-click also fires two clicks first; debounce the single-click so a
  // double-click doesn't first toggle direction and then get overridden.
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applySingleClickSort = (columnId: string) => {
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnId);
      // First click: numeric columns show high→low, text columns show A–Z.
      setSortDirection(textSet.has(columnId) ? 'asc' : 'desc');
    }
  };

  const handleHeaderClick = (columnId: string) => {
    if (clickTimer.current) clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => {
      applySingleClickSort(columnId);
      clickTimer.current = null;
    }, 200);
  };

  const handleHeaderDoubleClick = (columnId: string) => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    // Negatives-first == ascending: the most-negative value sorts to the top.
    setSortColumn(columnId);
    setSortDirection('asc');
  };

  const sortedItems = useMemo(() => {
    if (!sortColumn) return items;
    const isText = textSet.has(sortColumn);
    const dir = sortDirection === 'asc' ? 1 : -1;
    const read = (row: T) => (row as Record<string, unknown>)[sortColumn];
    return [...items].sort((a, b) => {
      const av = read(a);
      const bv = read(b);
      if (isText) {
        const as = av == null ? '' : String(av);
        const bs = bv == null ? '' : String(bv);
        // Blank cells always sink to the bottom, regardless of direction.
        if (!as && !bs) return 0;
        if (!as) return 1;
        if (!bs) return -1;
        return as.localeCompare(bs) * dir;
      }
      const an = parseFloat(String(av));
      const bn = parseFloat(String(bv));
      const aNaN = isNaN(an);
      const bNaN = isNaN(bn);
      // Non-numeric / blank cells always sink to the bottom.
      if (aNaN && bNaN) return 0;
      if (aNaN) return 1;
      if (bNaN) return -1;
      return (an - bn) * dir;
    });
  }, [items, sortColumn, sortDirection, textSet]);

  const getAriaSort = (columnId: string): 'ascending' | 'descending' | 'none' =>
    sortColumn === columnId ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none';

  return {
    sortColumn,
    sortDirection,
    sortedItems,
    handleHeaderClick,
    handleHeaderDoubleClick,
    getAriaSort,
  };
}
